import { Fragment, useCallback, useContext, useMemo } from 'react';

import {
  Stack,
  Chip,
  useTheme,
  Tooltip,
  Typography,
  Link,
  Theme
} from '@mui/material';

import parse, {
  HTMLReactParserOptions,
  Element,
  domToReact
} from 'html-react-parser';

import Card from '@/components/card';
import { CHARS } from '@/constants/description';
import { PostDetailsContext } from '@/contexts/PostDetailsContext';
import { ViolationContext } from '@/contexts/ViolationContext';
import { Platform, Posts } from '@/models/posts';
import {
  extractBrandNames,
  isLogoKeyword,
  toLogoKeyword,
  toPlainKeyword
} from '@/utils/logos';
import { highlightWords, replaceNewLinesWithBreaks } from '@/utils/text';

type charType = 'must' | 'might' | 'not';

interface KeywordProps {
  keyword: string;
  post: Posts.Details | null | undefined;
  onClickKeyword: (_value: string) => void;
  selectedKeywords: Array<string>;
}

interface LogoChipProps {
  brand: string;
  onClickKeyword: (_value: string) => void;
  selectedKeywords: Array<string>;
}

interface KeywordsStackProps {
  children: React.ReactNode;
}

interface HighlightedContentProps {
  content: string;
  mustAppear: number;
  mightAppear: number;
  theme: Theme;
  parseOptions?: HTMLReactParserOptions;
}

const checkMatch = (items: string[], matches: string[]) =>
  items.filter((item) => matches.includes(item));

const Keyword = ({
  keyword,
  post,
  onClickKeyword,
  selectedKeywords
}: KeywordProps) => {
  const getKeywordTooltip = useCallback(
    (_keyword: string) => {
      const keywordMatch = (post?.evaluation.keywordMatches || []).find(
        (match) => match.value === _keyword
      );

      return keywordMatch?.groups?.join(', ') ?? '';
    },
    [post]
  );

  return (
    <Tooltip
      arrow
      key={`${keyword}-${Math.random()}`}
      placement="top"
      title={getKeywordTooltip(keyword)}
    >
      <Chip
        clickable
        size="small"
        color="secondary"
        label={keyword}
        onClick={() => onClickKeyword(keyword)}
        variant={selectedKeywords.includes(keyword) ? 'filled' : 'outlined'}
      />
    </Tooltip>
  );
};

const LogoChip = ({
  brand,
  selectedKeywords,
  onClickKeyword
}: LogoChipProps) => (
  <Chip
    clickable
    size="small"
    color="primary"
    label={brand}
    onClick={() => onClickKeyword(toLogoKeyword(brand))}
    variant={
      selectedKeywords.includes(toLogoKeyword(brand)) ? 'filled' : 'outlined'
    }
  />
);

const KeywordsStack = ({ children }: KeywordsStackProps) => {
  const theme = useTheme();

  return (
    <Stack
      border={`1px solid ${theme.palette.grey[400]}`}
      borderRadius="4px"
      direction="row"
      gap={1}
      padding={1}
      flexWrap="wrap"
    >
      {children}
    </Stack>
  );
};

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (domNode instanceof Element) {
      const { attribs, children } = domNode;
      if (attribs?.href)
        return (
          <Link
            href={attribs.href.replace(/<[^>]*>/g, '')}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ display: 'block', marginBottom: 0.5, color: 'text.primary' }}
          >
            {domToReact(children, options)}
          </Link>
        );
    }
    return domNode;
  }
};

/**
 * HighlightedContent Component
 *
 * This component is designed to highlight specific sections of the content based on their importance.
 * It highlights sections as "must appear", "might appear", or "less likely to appear" using different styles and tooltips.
 *
 */
const HighlightedContent = ({
  content,
  mustAppear,
  mightAppear,
  theme,
  parseOptions
}: HighlightedContentProps) => {
  // Tracks the total character count processed so far
  let charCount = 0;

  /**
   * Determines the highlight type ('must', 'might', 'not') based on the character index.
   *
   * @param {number} index - The index of the character in the content.
   * @returns {charType} The type of highlight to apply to the character.
   */
  const getHighlightType = (index: number): charType => {
    if (index < mustAppear) return 'must';
    if (index < mustAppear + mightAppear) return 'might';
    return 'not';
  };

  /**
   * Returns the style object based on the highlight type.
   *
   * @param {charType} type - The type of highlight ('must', 'might', 'not').
   * @returns {object} The style object to be applied.
   */
  const getStyle = (type: charType): object => {
    switch (type) {
      case 'must':
        return {
          backgroundColor: `${theme.palette.success.main}40`, // Light green background
          cursor: 'pointer'
        };
      case 'might':
        return {
          backgroundColor: `${theme.palette.warning.main}33`, // Light yellow background
          cursor: 'pointer'
        };
      case 'not':
        return {
          backgroundColor: `${theme.palette.error.main}26`, // Light red background
          cursor: 'pointer'
        };
      default:
        return {};
    }
  };

  /**
   * Returns the tooltip text based on the highlight type.
   *
   * @param {charType} type - The type of highlight ('must', 'might', 'not').
   * @returns {string} The tooltip text to be displayed.
   */
  const getTooltipTitle = (type: charType): string => {
    switch (type) {
      case 'must':
        return 'This text is usually displayed directly';
      case 'might':
        return 'This text might be displayed directly, depending on device, browser and screen size.';
      case 'not':
        return "This text is usually only displayed after clicking 'read more'";
      default:
        return '';
    }
  };

  /**
   * Wraps a given text segment in a Tooltip with appropriate style and title.
   *
   */
  const wrapWithTooltip = (child: any, type: charType) => (
    <Tooltip placement="top" arrow title={getTooltipTitle(type)}>
      <span style={getStyle(type)}>{child}</span>
    </Tooltip>
  );

  /**
   * Custom replace function used by `html-react-parser` to process each node.
   *
   * @param {any} node - The current node being processed by the parser.
   * @returns {any} The processed node with highlights and tooltips, or undefined if not applicable.
   */
  const customReplace = (node: any): any => {
    // Checks if the node is a text node and has data (text content)
    if (node.type === 'text' && node.data) {
      const parts: React.ReactNode[] = [];
      let currentPart = '';
      let currentType = getHighlightType(charCount);

      // Iterates over each character in the text node
      for (let i = 0; i < node.data.length; i += 1) {
        const char = node.data[i];
        const newType = getHighlightType(charCount + i);

        // If the character type changes, push the current part and start a new one
        if (newType !== currentType) {
          parts.push(
            <Fragment key={charCount + i - currentPart.length}>
              {wrapWithTooltip(currentPart, currentType)}
            </Fragment>
          );
          currentPart = '';
          currentType = newType;
        }

        currentPart += char;
      }

      // Push the last part if any
      if (currentPart) {
        parts.push(
          <Fragment key={charCount + node.data.length - currentPart.length}>
            {wrapWithTooltip(currentPart, currentType)}
          </Fragment>
        );
      }

      // Update the total character count processed
      charCount += node.data.length;

      // Return the array of parts wrapped in a React fragment
      return <>{parts}</>;
    }

    return undefined;
  };

  // Parses the content and applies the custom replace function to highlight text
  return <>{parse(content, { ...parseOptions, replace: customReplace })}</>;
};

const DescriptionStack = ({
  description,
  platform,
  opt
}: {
  description: string;
  platform: Platform;
  opt: { sx: object; parseOptions: object };
}) => {
  const theme = useTheme();

  if (
    platform === 'youtube-livestream' ||
    platform === 'youtube-video' ||
    platform === 'youtube-short' ||
    platform === 'instagram-story'
  ) {
    return (
      <Stack>
        <Typography variant="body1" fontWeight="500">
          Description
        </Typography>
        <Typography sx={opt.sx} variant="caption">
          {parse(description, opt.parseOptions)}
        </Typography>
      </Stack>
    );
  }

  const { MUST_APPEAR, MIGHT_APPEAR } = CHARS[platform];

  return (
    <Stack>
      <Typography variant="body1" fontWeight="500">
        Description
      </Typography>
      <Typography sx={opt.sx} variant="caption">
        <HighlightedContent
          content={description}
          mustAppear={MUST_APPEAR}
          mightAppear={MIGHT_APPEAR}
          theme={theme}
          parseOptions={opt.parseOptions}
        />
      </Typography>
    </Stack>
  );
};

const TitleStack = ({
  title,
  platform
}: {
  title: string;
  platform: Platform;
}) => {
  const theme = useTheme();
  const parsedTitle = parse(title);

  if (platform !== 'youtube-short') {
    return (
      <Stack>
        <Typography variant="body1" fontWeight="500">
          Title
        </Typography>
        <Typography variant="caption">{parsedTitle}</Typography>
      </Stack>
    );
  }

  const { MUST_APPEAR, MIGHT_APPEAR } = CHARS[platform];

  return (
    <Stack>
      <Typography variant="body1" fontWeight="500">
        Title
      </Typography>
      <Typography variant="caption">
        <HighlightedContent
          content={title}
          mustAppear={MUST_APPEAR}
          mightAppear={MIGHT_APPEAR}
          theme={theme}
        />
      </Typography>
    </Stack>
  );
};

const PostKeywordsDetails = ({
  isViolation = false
}: {
  isViolation?: boolean;
}) => {
  const violationContext = useContext(ViolationContext);
  const postDetailsContext = useContext(PostDetailsContext);
  const context = isViolation ? violationContext : postDetailsContext;

  // Memoize context-derived values to avoid unnecessary recalculations/rerenders
  const data = useMemo(
    () =>
      isViolation ? (context as any)?.violationData : (context as any)?.data,
    [context, isViolation]
  );

  const selectedKeywords = useMemo(
    () => (isViolation ? [] : (context as any)?.selectedKeywords || []),
    [context, isViolation]
  );

  const setSelectedKeywords = useMemo(
    () => (isViolation ? () => {} : (context as any)?.setSelectedKeywords),
    [context, isViolation]
  );

  const keywordList = useMemo(
    () => (isViolation ? [] : (context as any)?.keywordList || []),
    [context, isViolation]
  );

  const logoList = useMemo(
    () => (isViolation ? [] : (context as any)?.logos || []),
    [context, isViolation]
  );

  const keywordMatches: string[] = (data?.evaluation?.keywordMatches ?? []).map(
    (match: { value: string }) => match.value
  );
  const brandMatches: string[] = data?.evaluation?.brandMatches ?? [];

  const visibleKeywords = checkMatch(keywordList, keywordMatches);
  const visibleBrands = checkMatch(extractBrandNames(logoList), brandMatches);

  const handleClickKeyword = useCallback(
    (keyword: string) => {
      if (isViolation) return;

      const counterpart = isLogoKeyword(keyword)
        ? toPlainKeyword(keyword)
        : toLogoKeyword(keyword);

      const wasSelected = selectedKeywords.includes(keyword);

      const newSelectedKeywords = selectedKeywords.filter(
        (k: string) => k !== keyword && k !== counterpart
      );

      if (!wasSelected) newSelectedKeywords.push(keyword);

      setSelectedKeywords(newSelectedKeywords);
    },
    [selectedKeywords, setSelectedKeywords, isViolation]
  );

  const highlightedContent = useMemo(() => {
    const tags =
      data?.tags
        ?.map((item: { username: string }) =>
          !item.username.startsWith('@') ? `@${item.username}` : item.username
        )
        .join(' ') || '';
    const hashtags =
      data?.hashtags
        ?.map((item: any) => {
          if (typeof item === 'object' && item !== null && item.hashtagName) {
            const hashtagName = item?.hashtagName;
            if (typeof hashtagName !== 'string') return null;
            return hashtagName.startsWith('#')
              ? hashtagName
              : `#${hashtagName}`;
          }

          if (typeof item === 'string') {
            return item.startsWith('#') ? item : `#${item}`;
          }

          return null;
        })
        .filter(Boolean)
        .join(' ') || '';
    const links =
      data?.links
        ?.map(
          (item: { url: string }) => `<a href="${item.url}">${item.url}</a>`
        )
        .join('') || '';
    const title = data?.title || '';
    const description = data?.description || '';

    const shouldHighlight = selectedKeywords.length > 0;

    const highlightedTags =
      shouldHighlight && tags ? highlightWords(tags, selectedKeywords) : tags;

    const highlightedHashtags =
      shouldHighlight && hashtags
        ? highlightWords(hashtags, selectedKeywords)
        : hashtags;

    const highlightedLinks =
      shouldHighlight && links
        ? highlightWords(links, selectedKeywords)
        : links;

    const highlightedTitle =
      shouldHighlight && title
        ? highlightWords(title, selectedKeywords)
        : title;

    let highlightedDescription =
      shouldHighlight && description
        ? highlightWords(description, selectedKeywords)
        : description;

    highlightedDescription = replaceNewLinesWithBreaks(highlightedDescription);

    return {
      tags: highlightedTags,
      hashtags: highlightedHashtags,
      links: highlightedLinks,
      title: highlightedTitle,
      description: highlightedDescription
    };
  }, [data, selectedKeywords]);

  const hasBorder = useMemo(
    () =>
      highlightedContent.tags.includes('</b>') ||
      highlightedContent.hashtags.includes('</b>') ||
      highlightedContent.links.includes('</b>') ||
      highlightedContent.title.includes('</b>') ||
      highlightedContent.description.includes('</b>'),
    [highlightedContent]
  );

  const hasKeywords =
    (visibleKeywords.length > 0 || visibleBrands.length > 0) && !isViolation;

  const keywordStack = hasKeywords && (
    <KeywordsStack>
      {visibleKeywords.map((keyword) => (
        <Keyword
          key={keyword}
          keyword={keyword}
          post={data}
          onClickKeyword={handleClickKeyword}
          selectedKeywords={selectedKeywords}
        />
      ))}
      {visibleBrands.map((brand) => (
        <LogoChip
          key={brand}
          brand={brand}
          onClickKeyword={handleClickKeyword}
          selectedKeywords={selectedKeywords}
        />
      ))}
    </KeywordsStack>
  );

  const createStack = (
    label: string,
    content: string,
    opt = { sx: {}, parseOptions: {} }
  ) =>
    content && (
      <Stack>
        <Typography variant="body1" fontWeight="500">
          {label}
        </Typography>
        <Typography sx={opt.sx} variant="caption">
          {parse(content, opt.parseOptions)}
        </Typography>
      </Stack>
    );

  if (!data) return <Typography variant="body2">Data not available</Typography>;

  const hasContent =
    (data?.title ||
      data?.description ||
      data?.tags?.length ||
      data?.hashtags?.length ||
      data?.links?.length ||
      visibleKeywords.length ||
      visibleBrands.length) &&
    !data.isExpired;

  if (!hasContent) return <></>;

  return (
    <Card
      sx={{ marginBottom: 2 }} // Replacing gap: 2 in PostLayout due to display block
      isCollapsible
      title={hasKeywords && !isViolation ? 'Keywords & Logos' : 'Details'}
      hasBorder={hasBorder}
      content={
        <Stack gap={2}>
          {keywordStack}
          {data?.title && (
            <TitleStack
              title={highlightedContent.title}
              platform={data.platform}
            />
          )}
          {data?.description && (
            <DescriptionStack
              description={highlightedContent.description}
              opt={{
                sx: { overflowY: 'auto', maxHeight: 240, paddingBottom: 1 },
                parseOptions: options
              }}
              platform={data.platform}
            />
          )}
          {createStack('Tags', highlightedContent.tags)}
          {createStack('Hashtags', highlightedContent.hashtags)}
          {createStack('Links', highlightedContent.links, {
            sx: { overflowY: 'auto', maxHeight: 240, paddingBottom: 1 },
            parseOptions: options
          })}
        </Stack>
      }
    />
  );
};

export default PostKeywordsDetails;
